import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Tab, Tabs, Table } from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import axiosInstance from "../services/axiosInterceptor";
import { getToken } from "../services/authorize";

const emptyCategoryForm = {
  category_id: "",
  category_code: "",
  category_name: "",
  category_status: "active",
  category_note: ""
};

const emptyLocationForm = {
  loc_id: "",
  loc_code: "",
  loc_name: "",
  loc_status: "active",
  loc_note: ""
};

const emptyShelfForm = {
  shelf_id: "",
  shelf_code: "",
  shelf_name: "",
  shelf_status: "active",
  shelf_note: "",
  loc_id: ""
};

const statusLabel = {
  active: "ใช้งาน",
  inactive: "ปิดใช้งาน"
};

const statusVariant = {
  active: "success",
  inactive: "secondary"
};

export default function LibraryMasterData() {
  const [activeTab, setActiveTab] = useState("categories");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [shelves, setShelves] = useState([]);

  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);
  const [locationForm, setLocationForm] = useState(emptyLocationForm);
  const [shelfForm, setShelfForm] = useState(emptyShelfForm);

  const headers = useMemo(() => ({ authorization: `Bearer ${getToken()}` }), []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [categoriesRes, locationsRes, shelvesRes] = await Promise.all([
        axiosInstance.get(`${process.env.REACT_APP_API}/library/getCategories`, { headers }),
        axiosInstance.get(`${process.env.REACT_APP_API}/library/getLocations`, { headers }),
        axiosInstance.get(`${process.env.REACT_APP_API}/library/getShelves`, { headers })
      ]);

      setCategories(categoriesRes.data || []);
      setLocations(locationsRes.data || []);
      setShelves(shelvesRes.data || []);
    } catch (fetchError) {
      setError("โหลดข้อมูลแม่บทห้องสมุดไม่สำเร็จ กรุณาตรวจสอบ schema และลองใหม่");
      console.error("LibraryMasterData fetch error:", fetchError);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const activeLocations = useMemo(
    () => locations.filter((location) => location.loc_status === "active"),
    [locations]
  );

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const mapApiMessage = (message, fallback) => {
    if (!message) return fallback;

    if (message === "missing required category fields") return "กรุณากรอกข้อมูลหมวดหมู่ให้ครบ";
    if (message === "duplicate category code") return "รหัสหมวดหมู่ซ้ำในระบบ";
    if (message === "duplicate category name") return "ชื่อหมวดหมู่ซ้ำในระบบ";
    if (message === "invalid category status") return "สถานะหมวดหมู่ไม่ถูกต้อง";
    if (message === "category not found") return "ไม่พบหมวดหมู่ที่เลือก";
    if (message === "category is in use") return "ปิดใช้งานไม่ได้ เพราะมีชื่อหนังสือที่ยังใช้งานในหมวดนี้";

    if (message === "missing required location fields") return "กรุณากรอกข้อมูลตำแหน่งจัดเก็บให้ครบ";
    if (message === "duplicate location code") return "รหัสตำแหน่งจัดเก็บซ้ำในระบบ";
    if (message === "duplicate location name") return "ชื่อตำแหน่งจัดเก็บซ้ำในระบบ";
    if (message === "invalid location status") return "สถานะตำแหน่งจัดเก็บไม่ถูกต้อง";
    if (message === "location not found") return "ไม่พบตำแหน่งจัดเก็บที่เลือก";
    if (message === "location is in use") return "ปิดใช้งานไม่ได้ เพราะมีเล่มหนังสือที่ยังใช้งานตำแหน่งนี้";

    if (message === "missing required shelf fields") return "กรุณากรอกข้อมูลรหัสชั้นวางให้ครบ";
    if (message === "duplicate shelf code") return "รหัสชั้นวางซ้ำในระบบ";
    if (message === "invalid shelf status") return "สถานะรหัสชั้นวางไม่ถูกต้อง";
    if (message === "shelf not found") return "ไม่พบรหัสชั้นวางที่เลือก";
    if (message === "shelf is in use") return "ปิดใช้งานไม่ได้ เพราะมีเล่มหนังสือที่ยังใช้รหัสชั้นวางนี้";

    return fallback;
  };

  const handleSaveCategory = async (event) => {
    event.preventDefault();
    clearMessages();

    try {
      setSaving(true);
      if (categoryForm.category_id) {
        await axiosInstance.post(
          `${process.env.REACT_APP_API}/library/updateCategory`,
          {
            category_id: Number(categoryForm.category_id),
            category_code: categoryForm.category_code,
            category_name: categoryForm.category_name,
            category_status: categoryForm.category_status,
            category_note: categoryForm.category_note || null
          },
          { headers }
        );
        setSuccess("แก้ไขหมวดหมู่สำเร็จ");
      } else {
        await axiosInstance.post(
          `${process.env.REACT_APP_API}/library/addCategory`,
          {
            category_code: categoryForm.category_code,
            category_name: categoryForm.category_name,
            category_note: categoryForm.category_note || null
          },
          { headers }
        );
        setSuccess("เพิ่มหมวดหมู่สำเร็จ");
      }

      setCategoryForm(emptyCategoryForm);
      await fetchData();
    } catch (saveError) {
      setError(
        mapApiMessage(
          saveError?.response?.data?.message,
          "บันทึกข้อมูลหมวดหมู่ไม่สำเร็จ"
        )
      );
      console.error("Save category error:", saveError);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLocation = async (event) => {
    event.preventDefault();
    clearMessages();

    try {
      setSaving(true);
      if (locationForm.loc_id) {
        await axiosInstance.post(
          `${process.env.REACT_APP_API}/library/updateLocation`,
          {
            loc_id: Number(locationForm.loc_id),
            loc_code: locationForm.loc_code,
            loc_name: locationForm.loc_name,
            loc_status: locationForm.loc_status,
            loc_note: locationForm.loc_note || null
          },
          { headers }
        );
        setSuccess("แก้ไขตำแหน่งจัดเก็บสำเร็จ");
      } else {
        await axiosInstance.post(
          `${process.env.REACT_APP_API}/library/addLocation`,
          {
            loc_code: locationForm.loc_code,
            loc_name: locationForm.loc_name,
            loc_note: locationForm.loc_note || null
          },
          { headers }
        );
        setSuccess("เพิ่มตำแหน่งจัดเก็บสำเร็จ");
      }

      setLocationForm(emptyLocationForm);
      await fetchData();
    } catch (saveError) {
      setError(
        mapApiMessage(
          saveError?.response?.data?.message,
          "บันทึกข้อมูลตำแหน่งจัดเก็บไม่สำเร็จ"
        )
      );
      console.error("Save location error:", saveError);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveShelf = async (event) => {
    event.preventDefault();
    clearMessages();

    try {
      setSaving(true);
      if (shelfForm.shelf_id) {
        await axiosInstance.post(
          `${process.env.REACT_APP_API}/library/updateShelf`,
          {
            shelf_id: Number(shelfForm.shelf_id),
            shelf_code: shelfForm.shelf_code,
            shelf_name: shelfForm.shelf_name || null,
            shelf_status: shelfForm.shelf_status,
            shelf_note: shelfForm.shelf_note || null,
            loc_id: shelfForm.loc_id ? Number(shelfForm.loc_id) : null
          },
          { headers }
        );
        setSuccess("แก้ไขรหัสชั้นวางสำเร็จ");
      } else {
        await axiosInstance.post(
          `${process.env.REACT_APP_API}/library/addShelf`,
          {
            shelf_code: shelfForm.shelf_code,
            shelf_name: shelfForm.shelf_name || null,
            shelf_note: shelfForm.shelf_note || null,
            loc_id: shelfForm.loc_id ? Number(shelfForm.loc_id) : null
          },
          { headers }
        );
        setSuccess("เพิ่มรหัสชั้นวางสำเร็จ");
      }

      setShelfForm(emptyShelfForm);
      await fetchData();
    } catch (saveError) {
      setError(
        mapApiMessage(
          saveError?.response?.data?.message,
          "บันทึกข้อมูลรหัสชั้นวางไม่สำเร็จ"
        )
      );
      console.error("Save shelf error:", saveError);
    } finally {
      setSaving(false);
    }
  };

  const deactivateCategory = async (categoryId) => {
    if (!window.confirm("ยืนยันปิดใช้งานหมวดหมู่นี้?")) return;
    clearMessages();

    try {
      await axiosInstance.post(
        `${process.env.REACT_APP_API}/library/deactivateCategory`,
        { category_id: categoryId },
        { headers }
      );
      setSuccess("ปิดใช้งานหมวดหมู่สำเร็จ");
      await fetchData();
    } catch (actionError) {
      setError(mapApiMessage(actionError?.response?.data?.message, "ปิดใช้งานหมวดหมู่ไม่สำเร็จ"));
      console.error("Deactivate category error:", actionError);
    }
  };

  const deactivateLocation = async (locationId) => {
    if (!window.confirm("ยืนยันปิดใช้งานตำแหน่งจัดเก็บนี้?")) return;
    clearMessages();

    try {
      await axiosInstance.post(
        `${process.env.REACT_APP_API}/library/deactivateLocation`,
        { loc_id: locationId },
        { headers }
      );
      setSuccess("ปิดใช้งานตำแหน่งจัดเก็บสำเร็จ");
      await fetchData();
    } catch (actionError) {
      setError(mapApiMessage(actionError?.response?.data?.message, "ปิดใช้งานตำแหน่งจัดเก็บไม่สำเร็จ"));
      console.error("Deactivate location error:", actionError);
    }
  };

  const deactivateShelf = async (shelfId) => {
    if (!window.confirm("ยืนยันปิดใช้งานรหัสชั้นวางนี้?")) return;
    clearMessages();

    try {
      await axiosInstance.post(
        `${process.env.REACT_APP_API}/library/deactivateShelf`,
        { shelf_id: shelfId },
        { headers }
      );
      setSuccess("ปิดใช้งานรหัสชั้นวางสำเร็จ");
      await fetchData();
    } catch (actionError) {
      setError(mapApiMessage(actionError?.response?.data?.message, "ปิดใช้งานรหัสชั้นวางไม่สำเร็จ"));
      console.error("Deactivate shelf error:", actionError);
    }
  };

  return (
    <React.Fragment>
      <Header />
      <main className="main main-app p-3 p-lg-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h4 className="mb-0">จัดการข้อมูลแม่บทห้องสมุด</h4>
          <Badge bg="secondary">Master Data</Badge>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Card>
          <Card.Body>
            <Tabs
              activeKey={activeTab}
              onSelect={(tabKey) => setActiveTab(tabKey || "categories")}
              className="mb-3"
            >
              <Tab eventKey="categories" title="หมวดหมู่">
                <Row className="g-3">
                  <Col lg={4}>
                    <Card>
                      <Card.Header className="fw-semibold">
                        {categoryForm.category_id ? "แก้ไขหมวดหมู่" : "เพิ่มหมวดหมู่"}
                      </Card.Header>
                      <Card.Body>
                        <Form onSubmit={handleSaveCategory}>
                          <Form.Group className="mb-2">
                            <Form.Label>รหัสหมวดหมู่</Form.Label>
                            <Form.Control
                              value={categoryForm.category_code}
                              onChange={(event) =>
                                setCategoryForm((prev) => ({ ...prev, category_code: event.target.value.toUpperCase() }))
                              }
                              required
                            />
                          </Form.Group>
                          <Form.Group className="mb-2">
                            <Form.Label>ชื่อหมวดหมู่</Form.Label>
                            <Form.Control
                              value={categoryForm.category_name}
                              onChange={(event) =>
                                setCategoryForm((prev) => ({ ...prev, category_name: event.target.value }))
                              }
                              required
                            />
                          </Form.Group>
                          <Form.Group className="mb-2">
                            <Form.Label>สถานะ</Form.Label>
                            <Form.Select
                              value={categoryForm.category_status}
                              onChange={(event) =>
                                setCategoryForm((prev) => ({ ...prev, category_status: event.target.value }))
                              }
                            >
                              <option value="active">ใช้งาน</option>
                              <option value="inactive">ปิดใช้งาน</option>
                            </Form.Select>
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label>หมายเหตุ</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={categoryForm.category_note}
                              onChange={(event) =>
                                setCategoryForm((prev) => ({ ...prev, category_note: event.target.value }))
                              }
                            />
                          </Form.Group>
                          <div className="d-flex gap-2">
                            <Button type="submit" disabled={saving}>
                              {saving ? "กำลังบันทึก..." : "บันทึก"}
                            </Button>
                            {categoryForm.category_id && (
                              <Button
                                variant="outline-secondary"
                                type="button"
                                onClick={() => setCategoryForm(emptyCategoryForm)}
                              >
                                ยกเลิกแก้ไข
                              </Button>
                            )}
                          </div>
                        </Form>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col lg={8}>
                    <Card>
                      <Card.Header className="fw-semibold d-flex align-items-center justify-content-between">
                        <span>รายการหมวดหมู่</span>
                        {loading && <Spinner animation="border" size="sm" />}
                      </Card.Header>
                      <Card.Body className="p-0">
                        <Table responsive hover className="mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>รหัส</th>
                              <th>ชื่อหมวดหมู่</th>
                              <th>จำนวนชื่อหนังสือ</th>
                              <th>สถานะ</th>
                              <th>จัดการ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {!loading && categories.length === 0 && (
                              <tr>
                                <td colSpan={5} className="text-center text-muted py-3">
                                  ไม่พบข้อมูลหมวดหมู่
                                </td>
                              </tr>
                            )}
                            {categories.map((category) => (
                              <tr key={category.category_id}>
                                <td>{category.category_code}</td>
                                <td>{category.category_name}</td>
                                <td>{Number(category.title_count || 0)}</td>
                                <td>
                                  <Badge bg={statusVariant[category.category_status] || "secondary"}>
                                    {statusLabel[category.category_status] || category.category_status}
                                  </Badge>
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() =>
                                        setCategoryForm({
                                          category_id: String(category.category_id),
                                          category_code: category.category_code || "",
                                          category_name: category.category_name || "",
                                          category_status: category.category_status || "active",
                                          category_note: category.category_note || ""
                                        })
                                      }
                                    >
                                      แก้ไข
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      disabled={category.category_status === "inactive"}
                                      onClick={() => deactivateCategory(category.category_id)}
                                    >
                                      ปิดใช้งาน
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Tab>

              <Tab eventKey="locations" title="ตำแหน่งจัดเก็บ">
                <Row className="g-3">
                  <Col lg={4}>
                    <Card>
                      <Card.Header className="fw-semibold">
                        {locationForm.loc_id ? "แก้ไขตำแหน่งจัดเก็บ" : "เพิ่มตำแหน่งจัดเก็บ"}
                      </Card.Header>
                      <Card.Body>
                        <Form onSubmit={handleSaveLocation}>
                          <Form.Group className="mb-2">
                            <Form.Label>รหัสตำแหน่ง</Form.Label>
                            <Form.Control
                              value={locationForm.loc_code}
                              onChange={(event) =>
                                setLocationForm((prev) => ({ ...prev, loc_code: event.target.value.toUpperCase() }))
                              }
                              required
                            />
                          </Form.Group>
                          <Form.Group className="mb-2">
                            <Form.Label>ชื่อตำแหน่ง</Form.Label>
                            <Form.Control
                              value={locationForm.loc_name}
                              onChange={(event) =>
                                setLocationForm((prev) => ({ ...prev, loc_name: event.target.value }))
                              }
                              required
                            />
                          </Form.Group>
                          <Form.Group className="mb-2">
                            <Form.Label>สถานะ</Form.Label>
                            <Form.Select
                              value={locationForm.loc_status}
                              onChange={(event) =>
                                setLocationForm((prev) => ({ ...prev, loc_status: event.target.value }))
                              }
                            >
                              <option value="active">ใช้งาน</option>
                              <option value="inactive">ปิดใช้งาน</option>
                            </Form.Select>
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label>หมายเหตุ</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={locationForm.loc_note}
                              onChange={(event) =>
                                setLocationForm((prev) => ({ ...prev, loc_note: event.target.value }))
                              }
                            />
                          </Form.Group>
                          <div className="d-flex gap-2">
                            <Button type="submit" disabled={saving}>
                              {saving ? "กำลังบันทึก..." : "บันทึก"}
                            </Button>
                            {locationForm.loc_id && (
                              <Button
                                variant="outline-secondary"
                                type="button"
                                onClick={() => setLocationForm(emptyLocationForm)}
                              >
                                ยกเลิกแก้ไข
                              </Button>
                            )}
                          </div>
                        </Form>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col lg={8}>
                    <Card>
                      <Card.Header className="fw-semibold d-flex align-items-center justify-content-between">
                        <span>รายการตำแหน่งจัดเก็บ</span>
                        {loading && <Spinner animation="border" size="sm" />}
                      </Card.Header>
                      <Card.Body className="p-0">
                        <Table responsive hover className="mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>รหัส</th>
                              <th>ชื่อตำแหน่ง</th>
                              <th>จำนวนเล่ม</th>
                              <th>สถานะ</th>
                              <th>จัดการ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {!loading && locations.length === 0 && (
                              <tr>
                                <td colSpan={5} className="text-center text-muted py-3">
                                  ไม่พบข้อมูลตำแหน่งจัดเก็บ
                                </td>
                              </tr>
                            )}
                            {locations.map((location) => (
                              <tr key={location.loc_id}>
                                <td>{location.loc_code}</td>
                                <td>{location.loc_name}</td>
                                <td>{Number(location.copy_count || 0)}</td>
                                <td>
                                  <Badge bg={statusVariant[location.loc_status] || "secondary"}>
                                    {statusLabel[location.loc_status] || location.loc_status}
                                  </Badge>
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() =>
                                        setLocationForm({
                                          loc_id: String(location.loc_id),
                                          loc_code: location.loc_code || "",
                                          loc_name: location.loc_name || "",
                                          loc_status: location.loc_status || "active",
                                          loc_note: location.loc_note || ""
                                        })
                                      }
                                    >
                                      แก้ไข
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      disabled={location.loc_status === "inactive"}
                                      onClick={() => deactivateLocation(location.loc_id)}
                                    >
                                      ปิดใช้งาน
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Tab>

              <Tab eventKey="shelves" title="รหัสชั้นวาง">
                <Row className="g-3">
                  <Col lg={4}>
                    <Card>
                      <Card.Header className="fw-semibold">
                        {shelfForm.shelf_id ? "แก้ไขรหัสชั้นวาง" : "เพิ่มรหัสชั้นวาง"}
                      </Card.Header>
                      <Card.Body>
                        <Form onSubmit={handleSaveShelf}>
                          <Form.Group className="mb-2">
                            <Form.Label>รหัสชั้นวาง</Form.Label>
                            <Form.Control
                              value={shelfForm.shelf_code}
                              onChange={(event) =>
                                setShelfForm((prev) => ({ ...prev, shelf_code: event.target.value.toUpperCase() }))
                              }
                              required
                            />
                          </Form.Group>
                          <Form.Group className="mb-2">
                            <Form.Label>ชื่อชั้นวาง</Form.Label>
                            <Form.Control
                              value={shelfForm.shelf_name}
                              onChange={(event) =>
                                setShelfForm((prev) => ({ ...prev, shelf_name: event.target.value }))
                              }
                            />
                          </Form.Group>
                          <Form.Group className="mb-2">
                            <Form.Label>ตำแหน่งจัดเก็บ</Form.Label>
                            <Form.Select
                              value={shelfForm.loc_id}
                              onChange={(event) =>
                                setShelfForm((prev) => ({ ...prev, loc_id: event.target.value }))
                              }
                            >
                              <option value="">ไม่ระบุ</option>
                              {activeLocations.map((location) => (
                                <option key={location.loc_id} value={location.loc_id}>
                                  {location.loc_code} - {location.loc_name}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                          <Form.Group className="mb-2">
                            <Form.Label>สถานะ</Form.Label>
                            <Form.Select
                              value={shelfForm.shelf_status}
                              onChange={(event) =>
                                setShelfForm((prev) => ({ ...prev, shelf_status: event.target.value }))
                              }
                            >
                              <option value="active">ใช้งาน</option>
                              <option value="inactive">ปิดใช้งาน</option>
                            </Form.Select>
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label>หมายเหตุ</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={shelfForm.shelf_note}
                              onChange={(event) =>
                                setShelfForm((prev) => ({ ...prev, shelf_note: event.target.value }))
                              }
                            />
                          </Form.Group>
                          <div className="d-flex gap-2">
                            <Button type="submit" disabled={saving}>
                              {saving ? "กำลังบันทึก..." : "บันทึก"}
                            </Button>
                            {shelfForm.shelf_id && (
                              <Button
                                variant="outline-secondary"
                                type="button"
                                onClick={() => setShelfForm(emptyShelfForm)}
                              >
                                ยกเลิกแก้ไข
                              </Button>
                            )}
                          </div>
                        </Form>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col lg={8}>
                    <Card>
                      <Card.Header className="fw-semibold d-flex align-items-center justify-content-between">
                        <span>รายการรหัสชั้นวาง</span>
                        {loading && <Spinner animation="border" size="sm" />}
                      </Card.Header>
                      <Card.Body className="p-0">
                        <Table responsive hover className="mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>รหัสชั้นวาง</th>
                              <th>ชื่อชั้นวาง</th>
                              <th>ตำแหน่ง</th>
                              <th>จำนวนเล่ม</th>
                              <th>สถานะ</th>
                              <th>จัดการ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {!loading && shelves.length === 0 && (
                              <tr>
                                <td colSpan={6} className="text-center text-muted py-3">
                                  ไม่พบข้อมูลรหัสชั้นวาง
                                </td>
                              </tr>
                            )}
                            {shelves.map((shelf) => (
                              <tr key={shelf.shelf_id}>
                                <td>{shelf.shelf_code}</td>
                                <td>{shelf.shelf_name || "-"}</td>
                                <td>{shelf.loc_name || "-"}</td>
                                <td>{Number(shelf.copy_count || 0)}</td>
                                <td>
                                  <Badge bg={statusVariant[shelf.shelf_status] || "secondary"}>
                                    {statusLabel[shelf.shelf_status] || shelf.shelf_status}
                                  </Badge>
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() =>
                                        setShelfForm({
                                          shelf_id: String(shelf.shelf_id),
                                          shelf_code: shelf.shelf_code || "",
                                          shelf_name: shelf.shelf_name || "",
                                          shelf_status: shelf.shelf_status || "active",
                                          shelf_note: shelf.shelf_note || "",
                                          loc_id: shelf.tb_library_location_loc_id
                                            ? String(shelf.tb_library_location_loc_id)
                                            : ""
                                        })
                                      }
                                    >
                                      แก้ไข
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      disabled={shelf.shelf_status === "inactive"}
                                      onClick={() => deactivateShelf(shelf.shelf_id)}
                                    >
                                      ปิดใช้งาน
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Tab>
            </Tabs>
          </Card.Body>
        </Card>
      </main>
      <Footer />
    </React.Fragment>
  );
}
